import { useCallback, useContext } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuid } from 'uuid'
import { AppContext } from '../App'
import { Condition } from '../proto/generated/nexus'
import {
    Broker_OrderType,
    Broker_Variety,
    Order,
    OrderStatus,
    OrderType,
    Product,
    TransactionType,
    Validity,
} from '../proto/generated/order'
import { Security } from '../proto/generated/security'
import { PlaceOrModifyOrdersRequest } from '../proto/generated/trading-bridge'
import { setAlertModalData } from '../reducer/alerts.reducer'
import { ensureTradingAccountForOrders, resolveOrderTradingAccountId } from '../util/orderPlacement'
import { updateOrderHistory } from '../reducer/orders.reducer'
import { TradeOrder } from '../reducer/strategyBuilder.reducer'
import { GroupedPosition } from '../reducer/positions.reducer'
import { ChartAlertSnapshot } from '../components/alerts/alertModal.typings'
import { selectActiveChartId } from '../selectors/chart'
import { selectOrdersHistory } from '../selectors/orders'
import { selectPrimaryTradingAccount, selectTradingAccounts } from '../selectors/user'
import store from '../store'
import { placeOrModifyOrder } from '../services/apis/orders'
import AnalyticsManager from '../analytics/AnalyticsManager'
import { addOrderAlertMessage } from '../util/orders'
import {
    buildQuickTradeChartSnapshot,
    buildStrategySpreadConditionalSnapshot,
    conditionalPrimaryUsesSpreadLhs,
    findBasketConditionalExitLeg,
    hasConditionalRuleContent,
    isConditionalEntryReady,
    mergeQuickTradeSnapshotWithMatchingChart,
    orderExecutionParamsForConditionalEntry,
    resolveAuxiliaryLegBatchId,
    resolveAuxiliaryLegWireIds,
    userAlertForConditionalEntryModal,
} from '../util/conditionalEntry'
import { registerConditionalEntryAlertModalHandlers } from '../util/conditionalEntryAlertModalBridge'
import { getSecurityKey } from '../util/security'

function tradeOrdersFromBasketChildren(children: NonNullable<GroupedPosition['children']>): TradeOrder[] {
    const lines: TradeOrder[] = []
    for (const c of children) {
        if (!c.security?.code || !c.quantity) continue
        lines.push({
            security: c.security,
            clientOrderId: uuid(),
            transactionType: c.quantity > 0 ? TransactionType.BUY : TransactionType.SELL,
            selected: true,
            quantity: Math.abs(c.quantity),
        })
    }
    return lines
}

export function useBasketPositionConditionalExit() {
    const dispatch = useDispatch()
    const appContext = useContext(AppContext)
    const activeChartId = useSelector(selectActiveChartId)
    const orderHistory = useSelector(selectOrdersHistory)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const tradingAccounts = useSelector(selectTradingAccounts)

    const openBasketConditionalExitModal = useCallback(
        async (params: {
            groupId: string
            underlyingSecurity: Security
            positionChildren: NonNullable<GroupedPosition['children']>
            strategyGroup?: string
        }) => {
            const { groupId, underlyingSecurity, positionChildren, strategyGroup } = params

            if (!ensureTradingAccountForOrders(dispatch, primaryTradingAccount, tradingAccounts)) {
                AnalyticsManager.logEvent('Place Order Clicked')
                return
            }

            const cxLeg = findBasketConditionalExitLeg(orderHistory, groupId, undefined)

            const sk = getSecurityKey(underlyingSecurity.exchange, underlyingSecurity.code)
            const snapMeta = store.getState().securityStore.snapshots[sk]
            const lastPrice = snapMeta?.lastTradedPrice ?? 0
            const lastVolume = snapMeta?.volume ?? 0

            const chartFn = appContext.getChartSnapshot[activeChartId]
            const chartSnap =
                typeof chartFn === 'function' ? (chartFn() as ChartAlertSnapshot | undefined) : undefined

            const tradeLines = tradeOrdersFromBasketChildren(positionChildren)
            const spreadSnap = buildStrategySpreadConditionalSnapshot(tradeLines, undefined)

            const underlyingBase = buildQuickTradeChartSnapshot(underlyingSecurity, 0, 0)
            const uMerged = mergeQuickTradeSnapshotWithMatchingChart(underlyingBase, chartSnap, underlyingSecurity)

            let merged: ChartAlertSnapshot
            let modalExtras:
                | {
                      conditionalModalChartVariants: {
                          underlying: ChartAlertSnapshot
                          spread: ChartAlertSnapshot | null
                      }
                      conditionalModalChartRef: 'underlying' | 'spread'
                  }
                | undefined

            const seedCondition = cxLeg?.orderExecutionParams?.condition

            if (spreadSnap) {
                const sMerged = mergeQuickTradeSnapshotWithMatchingChart(spreadSnap, chartSnap, underlyingSecurity)
                const initialRef: 'underlying' | 'spread' =
                    hasConditionalRuleContent(seedCondition) && !conditionalPrimaryUsesSpreadLhs(seedCondition)
                        ? 'underlying'
                        : 'spread'
                merged = initialRef === 'spread' ? sMerged : uMerged
                modalExtras = {
                    conditionalModalChartVariants: { underlying: uMerged, spread: sMerged },
                    conditionalModalChartRef: initialRef,
                }
            } else {
                const baseSnap = buildQuickTradeChartSnapshot(underlyingSecurity, lastPrice, lastVolume)
                merged = mergeQuickTradeSnapshotWithMatchingChart(baseSnap, chartSnap, underlyingSecurity)
            }

            const alert = userAlertForConditionalEntryModal(
                merged,
                hasConditionalRuleContent(seedCondition) ? seedCondition : undefined
            )

            const conditionalExitClientOrderId = cxLeg?.clientOrderId ?? uuid()

            const placeCx = async (condition: Condition) => {
                if (!isConditionalEntryReady({ enabled: true, condition })) return
                const exitParams = orderExecutionParamsForConditionalEntry(
                    cxLeg?.orderExecutionParams,
                    { enabled: true, condition },
                    { stripWhenDisabled: true }
                )
                if (!hasConditionalRuleContent(exitParams?.condition)) return

                const cxWire = resolveAuxiliaryLegWireIds({
                    existingLeg: cxLeg,
                    fallbackClientOrderId: conditionalExitClientOrderId,
                })
                const cxBatchId = resolveAuxiliaryLegBatchId({
                    orders: orderHistory,
                    basketId: groupId,
                    existingAuxLeg: cxLeg,
                })
                const orderWire: Order = {
                    security: { code: underlyingSecurity.code, exchange: underlyingSecurity.exchange },
                    transactionType: TransactionType.BUY,
                    orderType: OrderType.CONDITIONAL_EXIT,
                    brokerOrderType: Broker_OrderType.MARKET_ORDER,
                    quantity: 1,
                    isBasket: true,
                    basketId: groupId,
                    batchId: cxBatchId,
                    orderDate: Date.now(),
                    product: Product.CNC,
                    price: 0,
                    triggerPrice: 0,
                    brokerVariety: Broker_Variety.REGULAR,
                    validity: Validity.DAY,
                    clientOrderId: cxWire.clientOrderId,
                    orderId: cxWire.orderId,
                    immediateExecutePaperOrder: false,
                    orderExecutionParams: exitParams ?? {},
                    strategyGroup: strategyGroup ?? '',
                }

                const request: PlaceOrModifyOrdersRequest = {
                    tradingAccountId: resolveOrderTradingAccountId(primaryTradingAccount),
                    order: [orderWire],
                    buyBeforeSell: false,
                }

                try {
                    const response = await placeOrModifyOrder(request)
                    const updates: Order[] = []
                    let ok = 0
                    ;(response.orderResponse || []).forEach((orderResponse, i) => {
                        const { successful, errorMessage, order: respOrder } = orderResponse
                        if (!successful) {
                            addOrderAlertMessage({
                                ...request.order[i],
                                status: OrderStatus.FAILED,
                                message: errorMessage || 'Unable to place order',
                            })
                        } else {
                            ok++
                            if (respOrder) {
                                updates.push(respOrder)
                                addOrderAlertMessage({
                                    ...respOrder,
                                    message: 'Order placed successfully',
                                })
                            }
                        }
                    })
                    if (updates.length) dispatch(updateOrderHistory({ updates }))
                    if (ok === request.order.length) {
                        AnalyticsManager.logEvent('Basket conditional exit placed from positions')
                    }
                } catch (e: unknown) {
                    addOrderAlertMessage({
                        ...orderWire,
                        status: OrderStatus.FAILED,
                        message: e instanceof Error ? e.message : 'Unable to place order',
                    })
                }
            }

            registerConditionalEntryAlertModalHandlers(
                (condition: Condition) => {
                    void placeCx(condition)
                },
                () => {}
            )

            dispatch(
                setAlertModalData({
                    chartSnapshot: merged,
                    alert,
                    edit: false,
                    purpose: 'conditionalExit',
                    ...modalExtras,
                    conditionalUseFullIndicatorCatalog: true,
                })
            )
        },
        [
            activeChartId,
            appContext.getChartSnapshot,
            dispatch,
            orderHistory,
            primaryTradingAccount,
            tradingAccounts,
        ]
    )

    return { openBasketConditionalExitModal }
}
